import { create } from 'zustand';
import { fr, type TranslationKey } from './locales/fr';
import { en } from './locales/en';
import { ar } from './locales/ar';

export type Locale = 'fr' | 'en' | 'ar';

export const LOCALES: { code: Locale; label: string; dir: 'ltr' | 'rtl'; intl: string }[] = [
  { code: 'fr', label: 'Français', dir: 'ltr', intl: 'fr-FR' },
  { code: 'en', label: 'English', dir: 'ltr', intl: 'en-GB' },
  { code: 'ar', label: 'العربية', dir: 'rtl', intl: 'ar-DZ' },
];

const dictionaries: Record<Locale, Record<TranslationKey, string>> = { fr, en, ar };

const STORAGE_KEY = 'siraj.locale';

function readInitial(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'fr' || saved === 'en' || saved === 'ar') return saved;
  } catch {
    /* stockage indisponible */
  }
  return 'fr';
}

/** Applique langue et direction sur <html> (bascule LTR/RTL). */
export function applyLocaleToDocument(locale: Locale) {
  const meta = LOCALES.find((l) => l.code === locale)!;
  document.documentElement.lang = locale;
  document.documentElement.dir = meta.dir;
}

interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleState>((set) => ({
  locale: readInitial(),
  setLocale: (locale) => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* ignoré */
    }
    applyLocaleToDocument(locale);
    set({ locale });
  },
}));

export type TranslateParams = Record<string, string | number>;

/** Traduit une clé avec interpolation `{nom}`. Repli : français puis la clé brute. */
export function translate(locale: Locale, key: TranslationKey, params?: TranslateParams): string {
  const raw = dictionaries[locale][key] ?? fr[key] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name: string) => String(params[name] ?? `{${name}}`));
}
